import { createFileRoute } from '@tanstack/react-router';
import { useT } from '@/lib/i18n';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { auth } from '@/lib/auth';
import { useClients } from '@/lib/clients';
import { formatDateTime } from '@/lib/format';
import {
  MESSAGE_MAX,
  draftProblem,
  isSendKey,
  mergeMessages,
  messagesPath,
  useLatestMessages,
  useMarkMessagesRead,
  useSendMessage,
  type MessageList,
  type MessageView,
} from '@/lib/messages';
import { useOrgId } from '@/lib/org';
import { errorText, isClientRole, useOrgRole } from '@/lib/portal';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/messages')({ component: Messages });

/**
 * C5 · Messages (document 06): the one conversation between a client's people and Outss staff.
 * Oldest at the top, newest at the bottom; polled every 15 seconds while open.
 */
function Messages() {
  const orgId = useOrgId();
  const t = useT();
  if (!orgId)
    return <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />;
  // a fresh conversation (and its loaded history) per client
  return <Conversation key={orgId} orgId={orgId} />;
}

function Conversation({ orgId }: { orgId: string }) {
  const t = useT();
  const role = useOrgRole();
  const staff = role !== null && !isClientRole(role);
  const clients = useClients(auth.enabled);
  const clientName = clients.data?.find((c) => c.id === orgId)?.name ?? null;
  const latest = useLatestMessages();
  const [older, setOlder] = useState<MessageView[]>([]);
  const [olderHasMore, setOlderHasMore] = useState<boolean | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState<string | null>(null);
  const messages = mergeMessages(older, latest.data?.messages ?? []);
  const hasMore = olderHasMore ?? latest.data?.hasMore ?? false;
  const newestId = messages.at(-1)?.id;

  const scroller = useRef<HTMLDivElement>(null);
  /** scrollHeight before older messages are prepended, to keep the reader's place. */
  const keepPlace = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || keepPlace.current === null) return;
    el.scrollTop += el.scrollHeight - keepPlace.current;
    keepPlace.current = null;
  }, [older]);
  useEffect(() => {
    const el = scroller.current;
    if (el && newestId) el.scrollTop = el.scrollHeight;
  }, [newestId]);

  // reading the page marks the conversation read, again whenever a new message arrives
  const markRead = useMarkMessagesRead();
  const unread = latest.data?.unread ?? 0;
  const { mutate: markReadNow } = markRead;
  useEffect(() => {
    if (newestId && unread > 0) markReadNow();
  }, [newestId, unread, markReadNow]);

  const loadOlder = async () => {
    const before = messages[0]?.createdAt;
    if (!before) return;
    setLoadingOlder(true);
    setOlderError(null);
    try {
      const page = await api<MessageList>(messagesPath(orgId, { before }));
      keepPlace.current = scroller.current?.scrollHeight ?? null;
      setOlder((o) => mergeMessages(page.messages, o));
      setOlderHasMore(page.hasMore);
    } catch (e) {
      setOlderError(errorText(e));
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <div className="flex h-[calc(100dvh-7.5rem)] min-h-[420px] flex-col">
      <PageHeader
        title={t('messages.title')}
        description={
          staff
            ? t('messages.staffDescription', { client: clientName ?? t('messages.thisClient') })
            : t('messages.clientDescription')
        }
      />
      <div className="flex min-h-0 flex-1 flex-col rounded-lg border bg-card">
        <div
          ref={scroller}
          className="min-h-0 flex-1 overflow-y-auto p-4"
          data-testid="message-list"
          aria-live="polite"
        >
          {latest.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : latest.isError ? (
            <p className="text-sm text-destructive">
              {t('messages.loadError')} {errorText(latest.error)}
            </p>
          ) : !messages.length ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <p className="text-base font-medium">{t('messages.none')}</p>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                {staff ? t('messages.staffEmpty') : t('messages.clientEmpty')}
              </p>
            </div>
          ) : (
            <>
              {hasMore ? (
                <div className="mb-4 flex flex-col items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={loadingOlder}
                    onClick={() => void loadOlder()}
                  >
                    {loadingOlder ? t('common.loading') : t('messages.loadEarlier')}
                  </Button>
                  {olderError ? <p className="text-xs text-destructive">{olderError}</p> : null}
                </div>
              ) : null}
              <ol className="space-y-3">
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} clientName={clientName} />
                ))}
              </ol>
            </>
          )}
        </div>
        <Composer />
      </div>
    </div>
  );
}

function MessageBubble({
  message: m,
  clientName,
}: {
  message: MessageView;
  clientName: string | null;
}) {
  const t = useT();
  return (
    <li data-testid="message" className={cn('flex flex-col', m.mine ? 'items-end' : 'items-start')}>
      <div className="mb-1 flex max-w-full flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {m.mine ? t('messages.you') : m.author.name}
        </span>
        <span
          className={cn(
            'rounded-full border px-1.5 py-px text-[11px]',
            m.author.side === 'accountant' ? 'border-primary/30 text-primary' : '',
          )}
        >
          {m.author.side === 'accountant' ? 'Outss' : clientName?.trim() || t('messages.client')}
        </span>
        <time className="tnum" dateTime={m.createdAt}>
          {formatDateTime(m.createdAt)}
        </time>
      </div>
      <div
        className={cn(
          'max-w-[min(42rem,85%)] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words',
          m.mine ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
        )}
      >
        {m.body}
      </div>
    </li>
  );
}

function Composer() {
  const [draft, setDraft] = useState('');
  const send = useSendMessage();
  const problem = draftProblem(draft);
  const t = useT();
  const submit = () => {
    if (problem || send.isPending) return;
    send.mutate(draft, { onSuccess: () => setDraft('') });
  };
  return (
    <form
      className="border-t p-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Textarea
        aria-label={t('messages.message')}
        placeholder={t('messages.placeholder')}
        className="min-h-20 resize-y"
        maxLength={MESSAGE_MAX}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          if (send.isError) send.reset();
        }}
        onKeyDown={(e) => {
          if (isSendKey(e)) {
            e.preventDefault();
            submit();
          }
        }}
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 text-xs">
          {send.isError ? (
            <span className="text-destructive" role="alert">
              {errorText(send.error)}
            </span>
          ) : (
            <span className="text-muted-foreground">
              {t('messages.sendHint')} ·{' '}
              <span className="tnum">
                {draft.length.toLocaleString('en-GB')}/{MESSAGE_MAX.toLocaleString('en-GB')}
              </span>
            </span>
          )}
        </div>
        <Button type="submit" size="sm" disabled={Boolean(problem) || send.isPending}>
          {send.isPending ? t('messages.sending') : t('messages.send')}
        </Button>
      </div>
    </form>
  );
}
